import webview from '@adapter/webview';

import { isEmpty } from '@groww-tech-pvt/ella';
import { clearParticularKeyStorage } from './helper';
import { STORAGE_CONSTANTS, STORAGE_KEY_CONSTANTS, STORAGE_TYPE } from './constant';
import { errorInStorage, getStorage, setStorage, setStorageStrict } from '@adapter/web-storage';
const WV = webview();

export function clearNkeySeedAndSocketToken() {
  clearParticularKeyStorage(
    STORAGE_TYPE.LOCAL_COOKIE_STORAGE,
    STORAGE_CONSTANTS.N_KEY_SEED
  );
  clearParticularKeyStorage(
    STORAGE_TYPE.COOKIE,
    STORAGE_CONSTANTS.SOCKET_TOKEN
  );
}

export function getSocketToken() {
  const isEncrypted = WV.isActive ? false : true;

  const socketToken = getStorage(
    STORAGE_TYPE.COOKIE,
    STORAGE_CONSTANTS.SOCKET_TOKEN,
    isEncrypted
  );

  if (!isEmpty(socketToken)) {
    return socketToken as string;
  }

  return '';
}

// Already Implemented
export function resetPinToken() {
  clearParticularKeyStorage(STORAGE_TYPE.COOKIE, STORAGE_CONSTANTS.PIN_TOKEN);
  handleFnoActionsOnResetPinToken();
}

// Same Already Implemented
const handleFnoActionsOnResetPinToken = () => {
  setFnoRiskScreenSeenConsent(false);
};

export function setLoginRedirectionUrl(url: string) {
  setStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_KEY_CONSTANTS.REDIRECTION_URL,
    url,
    true
  );
}

// if the key is true then user has seen risk popup and provided consent for proceeding to place fno orders
export function setFnoRiskScreenSeenConsent(hasSeenPopup = true) {
  setStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_KEY_CONSTANTS.FNO_RISK_SCREEN,
    hasSeenPopup,
    false
  );
}

export function getDeviceUUID() {
  return getStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_CONSTANTS.USER_UUID,
    true
  );
}

export function getWebviewDeviceID() {
  return getStorage(
    STORAGE_TYPE.COOKIE,
    STORAGE_KEY_CONSTANTS.WEBVIEW_DEVICE_ID,
    false
  );
}

export function setDeviceUUID(
  uuid: string,
  expiry = STORAGE_CONSTANTS.EXPIRY_1_YEAR
) {
  setStorageStrict(
    STORAGE_TYPE.LOCAL_COOKIE_STORAGE,
    STORAGE_CONSTANTS.USER_UUID,
    uuid,
    true,
    expiry
  );
}

export function getAuthToken() {
  if (errorInStorage()) return '';

  const isEncrypted = WV.isActive ? false : true;

  const sessionId = getStorage(
    STORAGE_TYPE.LOCAL_COOKIE_STORAGE,
    STORAGE_CONSTANTS.AUTHORIZATION,
    isEncrypted
  );

  if (sessionId && sessionId != null && sessionId !== '') {
    return sessionId;
  }

  return '';
}

export function getSessionId() {
  return getAuthToken();
}

export function getPinToken(): string {
  if (errorInStorage()) return '';

  const isEncrypted = WV.isActive ? false : true;

  const pinTokenFromCookie = getStorage(
    STORAGE_TYPE.COOKIE,
    STORAGE_CONSTANTS.PIN_TOKEN,
    isEncrypted
  );

  //for groww-domain, we store the pin token in local storage
  const pinTokenFromLocalStorage = getStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_CONSTANTS.PIN_TOKEN,
    isEncrypted
  );

  const pinTokenFromLocalCookieStorage = getStorage(
    STORAGE_TYPE.LOCAL_COOKIE_STORAGE,
    STORAGE_CONSTANTS.PIN_TOKEN,
    isEncrypted
  );

  return pinTokenFromCookie || pinTokenFromLocalCookieStorage || pinTokenFromLocalStorage || '';
}

export function getRefreshToken() {
  const isEncrypted = WV.isActive ? false : true;

  return getStorage(
    STORAGE_TYPE.COOKIE,
    STORAGE_CONSTANTS.REFRESH_TOKEN,
    isEncrypted
  );
}

export function setRefreshToken(refreshToken: string) {
  // Backend has 30 days so it's good that we reduce it by 10 minutes
  const thirtyDaysMinusTenMinutes = STORAGE_CONSTANTS.THIRTY_DAYS_IN_MINS - 10;

  setStorageStrict(
    STORAGE_TYPE.COOKIE,
    STORAGE_CONSTANTS.REFRESH_TOKEN,
    refreshToken,
    true,
    thirtyDaysMinusTenMinutes
  );
}

// for webview, we don't encrypt the auth/1fa token
export function setAuthToken(sessionId: string, useEncryption: boolean = true) {
  // Backend has 30 days so it's good that we reduce it by 10 minutes
  const thirtyDaysMinusTenMinutes =
    STORAGE_CONSTANTS.THIRTY_DAYS_IN_MINS - STORAGE_CONSTANTS.TEN_MINUTES;

  setStorageStrict(
    STORAGE_TYPE.COOKIE,
    STORAGE_CONSTANTS.AUTHORIZATION,
    `Bearer ${sessionId}`,
    useEncryption,
    thirtyDaysMinusTenMinutes
  );
}

export function setPinToken(pinToken: string) {
  // Backend has 1 day expiry time. If we expire token before one day,
  // when pin is deleted from frontend localStorage, we will not send
  // pin token in api calls, since backend does not check for 2FA if
  // we don't send it in api, user will never know that pin is expired
  // unless they hit some critical api. Hence, we increased the expiry time from
  // oneDayMiusFiveMinutes to twoDaysMinusFiveMinutes so that backend gets the expired
  // pin token and sends 403 in response so that user is asked pin again
  // and things like socket work after that. Essentially we don't want the user
  // get stuck in a state whey they are 1FA and not 2FA.
  const twoDaysMinusFiveMinutes = STORAGE_CONSTANTS.TWO_DAYS_IN_MINS - 5;

  setStorageStrict(
    STORAGE_TYPE.LOCAL_COOKIE_STORAGE,
    STORAGE_CONSTANTS.PIN_TOKEN,
    `Bearer ${pinToken}`,
    true,
    twoDaysMinusFiveMinutes
  );
}

/** nats storage */
export function setSocketToken(token: string) {
  setStorageStrict(
    STORAGE_TYPE.COOKIE,
    STORAGE_CONSTANTS.SOCKET_TOKEN,
    token,
    true
  );
}

export function getLastRefreshedMarketCalendarData(): number | null {
  if (!errorInStorage()) {
    return getStorage(
      STORAGE_TYPE.LOCAL_STORAGE,
      STORAGE_KEY_CONSTANTS.LAST_REFRESHED_MARKET_CALENDAR_DATA,
      false
    );
  }

  return null;
}

export function setNowAsLastRefreshedMarketCalendarData() {
  if (!errorInStorage()) {
    setStorage(
      STORAGE_TYPE.LOCAL_STORAGE,
      STORAGE_KEY_CONSTANTS.LAST_REFRESHED_MARKET_CALENDAR_DATA,
      Date.now(),
      false
    );
  }
}

export function getStocksWalletBalanceCache() {
  return getStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_KEY_CONSTANTS.STOCKS_WALLET_BALANCE_CACHE,
    true
  );
}

export function setStocksWalletBalanceCache(walletData: any) {
  return setStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_KEY_CONSTANTS.STOCKS_WALLET_BALANCE_CACHE,
    walletData,
    true,
    STORAGE_CONSTANTS.ONE_DAY_IN_MINS
  );
}

export function setNkeySeed(encodedSeed: string) {
  setStorage(
    STORAGE_TYPE.COOKIE,
    STORAGE_CONSTANTS.N_KEY_SEED,
    encodedSeed,
    true
  );
}

export function clearLoginRedirectionUrl() {
  clearParticularKeyStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_KEY_CONSTANTS.REDIRECTION_URL
  );
}

export function setCustomWidgetUserPreference(
  key: string,
  value: any,
  expiry = STORAGE_CONSTANTS.ONE_DAY_IN_MINS
) {
  setStorage(STORAGE_TYPE.LOCAL_STORAGE, 'user-' + key, value, false, expiry);
}

export function getCustomWidgetUserPreference(key: string): any {
  return getStorage(STORAGE_TYPE.LOCAL_STORAGE, 'user-' + key, false);
}

export function setFloatingWindowPosition(coordinates: {
  x: number;
  y: number;
}) {
  setStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_KEY_CONSTANTS.FLOATING_WINDOW_POSITION,
    coordinates,
    false,
    STORAGE_CONSTANTS.THIRTY_DAYS_IN_MINS
  );
}

export function getFloatingWindowPosition(): { x: number; y: number } | null {
  const position = getStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_KEY_CONSTANTS.FLOATING_WINDOW_POSITION,
    false
  );

  return position;
}

export function getAccountAggCallBackIdCache() {
  return getStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_CONSTANTS.ACCOUNT_AGG_CALLBACK_ID,
    true
  );
}

export function setAccountAggCallBackIdCache(callBackId: string) {
  return setStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_CONSTANTS.ACCOUNT_AGG_CALLBACK_ID,
    callBackId,
    true,
    STORAGE_CONSTANTS.EXPIRY_15_MINUTES
  );
}

export function getActivateFnoOnboardingDataCache() {
  return getStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_CONSTANTS.FNO_ONBOARDING_DATA,
    true
  );
}

export function setActivateFnoOnboardingDataCache(data: any) {
  return setStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    STORAGE_CONSTANTS.FNO_ONBOARDING_DATA,
    data,
    true,
    STORAGE_CONSTANTS.EXPIRY_15_MINUTES
  );
}
